/**
 * Session Player illustration — miniature wireframe of the lesson session:
 * segmented progress bar, question text, 2×2 MCQ option grid, check button.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function SessionPlayer({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 120 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Window frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="152"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Segmented progress bar */}
      <rect x="14" y="16" width="18" height="3" rx="1.5" fill={p.accent1} />
      <rect x="35" y="16" width="18" height="3" rx="1.5" fill={p.accent2} />
      <rect x="56" y="16" width="18" height="3" rx="1.5" fill={p.strong} />
      <rect x="77" y="16" width="18" height="3" rx="1.5" fill={p.faint} />
      <rect x="98" y="16" width="7" height="3" rx="1.5" fill={p.faint} />

      {/* Question text */}
      <rect x="14" y="32" width="86" height="4" rx="2" fill={p.medium} />
      <rect x="14" y="40" width="62" height="4" rx="2" fill={p.faint} />

      {/* 2×2 MCQ grid */}
      <rect
        x="14"
        y="56"
        width="43"
        height="28"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="20" y="66" width="28" height="3" rx="1.5" fill={p.subtle} />
      <rect x="20" y="72" width="18" height="3" rx="1.5" fill={p.faint} />

      <rect
        x="63"
        y="56"
        width="43"
        height="28"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="69" y="66" width="24" height="3" rx="1.5" fill={p.subtle} />
      <rect x="69" y="72" width="30" height="3" rx="1.5" fill={p.faint} />

      <rect
        x="14"
        y="90"
        width="43"
        height="28"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="20" y="100" width="32" height="3" rx="1.5" fill={p.subtle} />
      <rect x="20" y="106" width="20" height="3" rx="1.5" fill={p.faint} />

      <rect
        x="63"
        y="90"
        width="43"
        height="28"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="69" y="100" width="26" height="3" rx="1.5" fill={p.subtle} />
      <rect x="69" y="106" width="22" height="3" rx="1.5" fill={p.faint} />

      {/* Check button */}
      <rect x="32" y="132" width="56" height="14" rx="7" fill={p.buttonFill} />
      <rect x="47" y="137" width="26" height="3.5" rx="1.75" fill={p.buttonText} />
    </svg>
  );
}
